// ChoiceGroup and Choice (audit §4b): a labelled group of choices in a pop-over (the filter's State,
// Department, Job type). 30 px, --fs-table, framed with a hairline; a chosen one is filled in ink
// (DESIGN.md §8). Several at once is a group of checkboxes; one of them, a radio group, so a screen
// reader says which it is. A choice may carry a count ("On the way 2"), or a muted second line saying
// what it does (the timesheet's Fix). A choice whose things wait on someone (the price book's "Asked by
// techs": the office has to answer them) is framed dashed, DESIGN.md's waiting drawing, until it's
// chosen. The group may carry a muted line under its choices saying what ticking one means (a job type's
// Licences it needs), drawn as a field's description; with hideLabel its heading is for screen readers
// only, where a setting's row names it. Stacked, they read down one to a line (the board's
// Pause for). Reads only --choice-* and --choices-*.
import { createContext, useContext, type ReactNode } from 'react';
import { Checkbox, CheckboxGroup, Label, Radio, RadioGroup, Text } from 'react-aria-components';
import './ChoiceGroup.css';

type Mode = 'single' | 'multiple';
const ModeContext = createContext<Mode>('multiple');

interface GroupCommon {
  /** The group's heading, shown ("State"). */
  label: string;
  /** Keeps the heading for screen readers only, where what's around it names the choices (a setting's row). */
  hideLabel?: boolean;
  children: ReactNode;
  /** A muted line under the choices, saying what ticking one means; read out with the group. */
  description?: ReactNode;
  isDisabled?: boolean;
  /**
   * flow (the default): side by side, wrapping onto as many lines as they need. stack: one to a line at the
   * group's full width, a short list read down (the board's Pause for, each reason with its hint).
   */
  layout?: 'flow' | 'stack';
}
export type ChoiceGroupProps =
  | (GroupCommon & { selectionMode?: 'multiple'; value: string[]; onChange?: (value: string[]) => void })
  | (GroupCommon & { selectionMode: 'single'; value: string; onChange?: (value: string) => void });

export function ChoiceGroup(props: ChoiceGroupProps) {
  const head = (
    <Label className="fl-choices__label" data-hidden={props.hideLabel ? '' : undefined}>
      {props.label}
    </Label>
  );
  const list = (
    <>
      <div className="fl-choices__list" data-layout={props.layout === 'stack' ? 'stack' : undefined}>
        {props.children}
      </div>
      {props.description ? (
        <Text slot="description" className="fl-choices__desc">
          {props.description}
        </Text>
      ) : null}
    </>
  );
  if (props.selectionMode === 'single')
    return (
      <ModeContext.Provider value="single">
        <RadioGroup
          className="fl-choices"
          value={props.value}
          onChange={props.onChange}
          isDisabled={props.isDisabled}
          orientation="horizontal"
        >
          {head}
          {list}
        </RadioGroup>
      </ModeContext.Provider>
    );
  return (
    <ModeContext.Provider value="multiple">
      <CheckboxGroup
        className="fl-choices"
        value={props.value}
        onChange={props.onChange}
        isDisabled={props.isDisabled}
      >
        {head}
        {list}
      </CheckboxGroup>
    </ModeContext.Provider>
  );
}

export interface ChoiceProps {
  value: string;
  children: ReactNode;
  /** How many it would show, after the words. */
  count?: number;
  /**
   * A muted second line under the words, saying what it does ("Adds 2 h 20 min of work"): the
   * timesheet's Fix choices. The choice then grows to two lines.
   */
  description?: ReactNode;
  isDisabled?: boolean;
  /** Its things wait on someone (Asked by techs): framed dashed until it's chosen. */
  isWaiting?: boolean;
}

export function Choice({ value, children, count, description, isDisabled, isWaiting }: ChoiceProps) {
  const mode = useContext(ModeContext);
  const words = (
    <>
      <span className="fl-choice__label">{children}</span>
      {count === undefined ? null : <span className="fl-choice__count">{count}</span>}
    </>
  );
  const face = description ? (
    <>
      <span className="fl-choice__line">{words}</span>
      <span className="fl-choice__desc">{description}</span>
    </>
  ) : (
    words
  );
  const described = description ? '' : undefined;
  const waiting = isWaiting ? '' : undefined;
  return mode === 'single' ? (
    <Radio
      className="fl-choice"
      value={value}
      isDisabled={isDisabled}
      data-described={described}
      data-waiting={waiting}
    >
      {face}
    </Radio>
  ) : (
    <Checkbox
      className="fl-choice"
      value={value}
      isDisabled={isDisabled}
      data-described={described}
      data-waiting={waiting}
    >
      {face}
    </Checkbox>
  );
}
